import React from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency } from '../../config/currency';
import type { FinanceInsight, InsightSeverity } from '../../api/financeApi';

interface Props {
  insight: FinanceInsight;
  onOpen: (insight: FinanceInsight) => void;
  onAct: (insight: FinanceInsight) => void;
  onDismiss: (id: number) => void;
  onSnooze: (id: number, days: number) => void;
  onHideType: (type: string) => void;
}

const sevColor = (sev: InsightSeverity): string => {
  switch (sev) {
    case 'CRITICAL':
      return '#D32F2F';
    case 'WARN':
      return '#ED6C02';
    default:
      return '#0288D1';
  }
};

const actionLabel = (actionType: string | null): string => {
  switch (actionType) {
    case 'REVIEW_EOD_CLOSES':
      return 'Review closes';
    case 'COMPARE_EXPENSES':
      return 'Compare bills';
    case 'OPEN_STAFF_PAYMENTS':
      return 'Open staff payments';
    case 'OPEN_RECURRING_TEMPLATE':
      return 'Pay now';
    case 'OPEN_STAFF_PROFILE':
      return 'Open staff';
    case 'OPEN_CATEGORY':
      return 'Open category';
    default:
      return 'Open';
  }
};

const InsightCard: React.FC<Props> = ({ insight, onOpen, onAct, onDismiss, onSnooze, onHideType }) => {
  const { theme } = useTheme();
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const color = sevColor(insight.severity);
  const impact = insight.impactAmount;

  return (
    <Paper
      elevation={0}
      onClick={() => onOpen(insight)}
      sx={{
        p: 1.25,
        cursor: 'pointer',
        borderLeft: `4px solid ${color}`,
        border: `1px solid ${theme.gray?.[200] || '#EEE'}`,
        borderLeftWidth: 4,
        borderLeftColor: color,
        background: theme.components?.card?.background || '#FFFFFF',
        transition: 'background 0.12s ease',
        '&:hover': { background: `${color}08` },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '0.92rem',
              lineHeight: 1.25,
              color: theme.text,
              textDecorationLine: 'underline',
              textDecorationColor: 'transparent',
              '&:hover': { textDecorationColor: theme.gray?.[400] },
            }}
          >
            <Box
              component="span"
              aria-hidden
              sx={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: color,
                mr: 0.75,
                verticalAlign: 'middle',
              }}
            />
            {insight.title}
          </Typography>
          <Typography
            sx={{
              color: theme.gray?.[500] || '#666',
              fontSize: '0.78rem',
              mt: 0.4,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {insight.body}
          </Typography>
          <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
            <Button
              size="small"
              variant="contained"
              onClick={(e) => {
                e.stopPropagation();
                onAct(insight);
              }}
              sx={{
                background: color,
                textTransform: 'none',
                fontSize: '0.74rem',
                py: 0.25,
                px: 1.25,
                minWidth: 0,
                '&:hover': { background: color, opacity: 0.9 },
                '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: '2px' },
              }}
            >
              {actionLabel(insight.actionType)}
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={(e) => {
                e.stopPropagation();
                onOpen(insight);
              }}
              sx={{
                textTransform: 'none',
                fontSize: '0.74rem',
                py: 0.25,
                px: 1.25,
                minWidth: 0,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                color: theme.gray?.[500],
                '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: '2px' },
              }}
            >
              Why?
            </Button>
          </Stack>
        </Box>
        <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
          {impact != null && (
            <Chip
              size="small"
              label={`${impact < 0 ? '-' : ''}${formatCurrency(Math.abs(impact))}${
                insight.impactPeriod === 'WEEKLY'
                  ? '/wk'
                  : insight.impactPeriod === 'MONTHLY'
                  ? '/mo'
                  : ''
              }`}
              sx={{
                background: `${color}1F`,
                color,
                fontWeight: 700,
                fontSize: '0.72rem',
                height: 22,
              }}
            />
          )}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
            }}
          >
            <EllipsisVerticalIcon style={{ width: 16, height: 16 }} />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem
              onClick={() => {
                onSnooze(insight.id, 7);
                setMenuAnchor(null);
              }}
            >
              Snooze 7 days
            </MenuItem>
            <MenuItem
              onClick={() => {
                onSnooze(insight.id, 30);
                setMenuAnchor(null);
              }}
            >
              Snooze 30 days
            </MenuItem>
            <MenuItem
              onClick={() => {
                onDismiss(insight.id);
                setMenuAnchor(null);
              }}
            >
              Dismiss
            </MenuItem>
            <MenuItem
              onClick={() => {
                onHideType(insight.insightType);
                setMenuAnchor(null);
              }}
              sx={{ color: 'var(--status-bad)' }}
            >
              Not useful — hide type 30 days
            </MenuItem>
          </Menu>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default InsightCard;
